import React, {createContext, useContext, useEffect, useState, useCallback} from "react"
import moment from "moment-timezone"
import {useLocalStorage} from "react-use"

export type TimezoneOption = "browser" | "UTC" | "EST" | "CST" | "PST"

interface TimezoneContextType {
	timezone: TimezoneOption
	setTimezone: (timezone: TimezoneOption) => void
	getTimezoneLabel: (timezone: TimezoneOption) => string
	getTimezoneDisplayText: (timezone: TimezoneOption) => string
	getTimezoneIANA: (timezone: TimezoneOption) => string
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined)

const TIMEZONE_STORAGE_KEY = "selectedTimezone"

// Timezone configuration with identifiers and display information
interface TimezoneConfig {
	identifier: string
	displayText: string
	fullLabel: string
}

const timezoneConfig: Record<Exclude<TimezoneOption, "browser">, TimezoneConfig> = {
	UTC: {
		identifier: "UTC",
		displayText: "UTC",
		fullLabel: "UTC (GMT+0:00)",
	},
	EST: {
		identifier: "America/New_York",
		displayText: "Eastern Time",
		fullLabel: "Eastern Time",
	},
	CST: {
		identifier: "America/Chicago",
		displayText: "Central Time",
		fullLabel: "Central Time",
	},
	PST: {
		identifier: "America/Los_Angeles",
		displayText: "Pacific Time",
		fullLabel: "Pacific Time",
	},
}

// Validate if a string is a valid timezone option
function isValidTimezoneOption(value: string | undefined): value is TimezoneOption {
	return value === "browser" || value === "UTC" || value === "EST" || value === "CST" || value === "PST"
}

export function TimezoneProvider({children}: {children: React.ReactNode}) {
	const [storedTimezone, setStoredTimezone] = useLocalStorage<TimezoneOption>(TIMEZONE_STORAGE_KEY, "browser")
	const [timezone, setTimezoneState] = useState<TimezoneOption>(() => {
		if (storedTimezone && isValidTimezoneOption(storedTimezone)) {
			return storedTimezone
		}
		return "browser"
	})

	const getTimezoneIANA = useCallback((tz: TimezoneOption): string => {
		if (tz === "browser") {
			return moment.tz.guess()
		}
		return timezoneConfig[tz].identifier
	}, [])

	useEffect(() => {
		// Apply the timezone setting to moment
		const ianaTimezone = getTimezoneIANA(timezone)
		if (ianaTimezone) {
			moment.tz.setDefault(ianaTimezone)
		} else {
			// Reset to local time
			moment.tz.setDefault()
		}
	}, [timezone, getTimezoneIANA])

	const setTimezone = (newTimezone: TimezoneOption) => {
		setTimezoneState(newTimezone)
		setStoredTimezone(newTimezone)
		// Reload the page to ensure all components re-render with the new timezone
		window.location.reload()
	}

	const getTimezoneLabel = (tz: TimezoneOption): string => {
		if (tz === "browser") {
			const browserTz = moment.tz.guess()
			const offset = moment.tz(browserTz).format("Z")
			return `GMT${offset} (Browser Time Zone)`
		}
		return timezoneConfig[tz].fullLabel
	}

	const getTimezoneDisplayText = (tz: TimezoneOption): string => {
		if (tz === "browser") {
			const browserTz = moment.tz.guess()
			const offset = moment.tz(browserTz).format("Z")
			return `GMT${offset}`
		}
		return timezoneConfig[tz].displayText
	}

	return (
		<TimezoneContext.Provider
			value={{timezone, setTimezone, getTimezoneLabel, getTimezoneDisplayText, getTimezoneIANA}}
		>
			{children}
		</TimezoneContext.Provider>
	)
}

export function useTimezone() {
	const context = useContext(TimezoneContext)
	if (!context) {
		throw new Error("useTimezone must be used within a TimezoneProvider")
	}
	return context
}
